import config from "@Config/index";
import axios from "axios";

export interface PropertyFilterOption {
  value: string;
  count: number;
}

export interface PropertyFilterStayDuration {
  key: string;
  name: string;
  value: string;
  group: string;
  count: number;
}

export interface PropertyFilterMoveInMonth {
  value: string;
  name: string;
  count: number;
}

export interface PropertyFilterDualOccupancy {
  available: boolean;
  count: number;
}

export interface PropertyFilterOptions {
  room_type: {
    room_types: PropertyFilterOption[];
    accommodation: PropertyFilterOption[];
    kitchen: PropertyFilterOption[];
    bathroom: PropertyFilterOption[];
  };
  stay_duration: PropertyFilterStayDuration[];
  move_in_month: PropertyFilterMoveInMonth[];
  dual_occupancy: PropertyFilterDualOccupancy;
}

/**
 * Fetches dynamic filter options for a specific property from the backend.
 * Returns only filter values that have available inventory (count > 0).
 * Deduplicates concurrent requests for the same inventoryId.
 *
 * @param inventoryId - The property/inventory ID
 * @returns Structured filter options or null on error
 */

// Cache in-flight requests to prevent duplicate API calls from multiple component instances
let inFlightRequest: {
  id: string | number;
  promise: Promise<PropertyFilterOptions | null>;
} | null = null;

const fetchPropertyFilterOptions = async (
  inventoryId: number | string,
  duration: "weekly" | "monthly" = "weekly",
): Promise<PropertyFilterOptions | null> => {
  // If a request for the same inventoryId is already in-flight, reuse it
  if (inFlightRequest && inFlightRequest.id === inventoryId) {
    return inFlightRequest.promise;
  }

  const promise = (async () => {
    try {
      const response = await axios.get(
        `${config.API_URL}/api/v1/inventories/inventory_filter_count`,
        {
          params: {
            id: inventoryId,
            agg_keys:
              "room_types,bathroom_types,kitchen_types,move_in_month,lease_duration,dual_occupancy",
            duration,
            use_java_api: true,
            use_v2_structure: true,
          },
          headers: { "X-Use-Java-Api": "true" },
        },
      );

      const data = response?.data?.data?.data;
      if (!data) return null;

      return data as PropertyFilterOptions;
    } catch (error) {
      console.error("[fetchPropertyFilterOptions] Failed to fetch:", error);
      return null;
    } finally {
      inFlightRequest = null;
    }
  })();

  inFlightRequest = { id: inventoryId, promise };
  return promise;
};

export default fetchPropertyFilterOptions;
